/**
 * Task Tracker - Ensures AI agents document remaining work
 * 
 * WHAT NOBODY ELSE IS DOING:
 * - Forces AI to document what's remaining before stopping
 * - Creates checkpoints for task resumption
 * - Tracks progress across sessions
 * - Prevents abandoned incomplete work
 */

import * as fs from 'fs';
import * as path from 'path';

export interface TaskStep {
  id: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'blocked' | 'skipped';
  startedAt?: string;
  completedAt?: string;
  notes?: string;
  blockedReason?: string;
  filesModified?: string[];
  dependencies?: string[];
}

export interface TaskCheckpoint {
  id: string;
  taskId: string;
  timestamp: string;
  currentStep: number;
  totalSteps: number;
  completedSteps: string[];
  remainingSteps: TaskStep[];
  contextSnapshot: ContextSnapshot;
  resumptionNotes: string;
  canResume: boolean;
}

export interface ContextSnapshot {
  openFiles: string[];
  lastModifiedFile?: string;
  cursorPosition?: { file: string; line: number };
  relevantVariables?: Record<string, string>;
  environmentState?: Record<string, string>;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
  steps: TaskStep[];
  checkpoints: TaskCheckpoint[];
  totalEstimatedSteps: number;
  completionPercentage: number;
  lastActiveAgent?: string;
  tags?: string[];
}

export interface TaskSummary {
  completed: string[];
  inProgress: string[];
  remaining: string[];
  blocked: string[];
  estimatedRemainingWork: string;
  nextSteps: string[];
  warnings: string[];
}

export interface StopValidation {
  canStop: boolean;
  mustDocument: string[];
  warnings: string[];
  suggestedCheckpoint: Partial<TaskCheckpoint>;
}

export class TaskTracker {
  private workspaceRoot: string;
  private tasksPath: string;
  private currentTask: Task | null = null;
  private tasks: Map<string, Task> = new Map();

  constructor(workspaceRoot: string) {
    this.workspaceRoot = workspaceRoot;
    this.tasksPath = path.join(workspaceRoot, '.guardrail', 'tasks.json');
    this.loadTasks();
  }

  /**
   * Start a new task
   */
  startTask(title: string, description: string, estimatedSteps?: string[]): Task {
    const task: Task = {
      id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title,
      description,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'active',
      steps: estimatedSteps?.map((desc, i) => ({
        id: `step-${i + 1}`,
        description: desc,
        status: 'pending',
      })) || [],
      checkpoints: [],
      totalEstimatedSteps: estimatedSteps?.length || 0,
      completionPercentage: 0,
    };

    this.tasks.set(task.id, task);
    this.currentTask = task;
    this.saveTasks();

    return task;
  }

  /**
   * Add steps to current task
   */
  addSteps(steps: string[]): void {
    if (!this.currentTask) {
      throw new Error('No active task. Call startTask first.');
    }

    const newSteps: TaskStep[] = steps.map((desc, i) => ({
      id: `step-${this.currentTask!.steps.length + i + 1}`,
      description: desc,
      status: 'pending',
    }));

    this.currentTask.steps.push(...newSteps);
    this.currentTask.totalEstimatedSteps = this.currentTask.steps.length;
    this.currentTask.updatedAt = new Date().toISOString();
    this.saveTasks();
  }

  /**
   * Update step status
   */
  updateStep(stepId: string, status: TaskStep['status'], notes?: string): void {
    if (!this.currentTask) return;

    const step = this.currentTask.steps.find(s => s.id === stepId);
    if (!step) return;

    step.status = status;
    if (notes) step.notes = notes;

    if (status === 'in_progress' && !step.startedAt) {
      step.startedAt = new Date().toISOString();
    }
    if (status === 'completed') {
      step.completedAt = new Date().toISOString();
    }

    this.updateCompletionPercentage();
    this.currentTask.updatedAt = new Date().toISOString();
    this.saveTasks();
  }

  /**
   * Mark current step as in progress
   */
  startStep(stepId: string): void {
    this.updateStep(stepId, 'in_progress');
  }

  /**
   * Complete current step
   */
  completeStep(stepId: string, notes?: string, filesModified?: string[]): void {
    if (!this.currentTask) return;

    const step = this.currentTask.steps.find(s => s.id === stepId);
    if (step) {
      step.status = 'completed';
      step.completedAt = new Date().toISOString();
      if (notes) step.notes = notes;
      if (filesModified) step.filesModified = filesModified;
    }

    this.updateCompletionPercentage();
    this.saveTasks();
  }

  /**
   * Mark step as blocked
   */
  blockStep(stepId: string, reason: string): void {
    if (!this.currentTask) return;

    const step = this.currentTask.steps.find(s => s.id === stepId);
    if (step) {
      step.status = 'blocked';
      step.blockedReason = reason;
    }

    this.saveTasks();
  }

  /**
   * CRITICAL: Validate if agent can stop working
   * This is the key feature - prevents stopping without documenting remaining work
   */
  validateStop(): StopValidation {
    if (!this.currentTask) {
      return {
        canStop: true,
        mustDocument: [],
        warnings: ['No active task tracked'],
        suggestedCheckpoint: {},
      };
    }

    const mustDocument: string[] = [];
    const warnings: string[] = [];

    // Check for in-progress steps
    const inProgressSteps = this.currentTask.steps.filter(s => s.status === 'in_progress');
    if (inProgressSteps.length > 0) {
      mustDocument.push(`Document status of in-progress step(s): ${inProgressSteps.map(s => s.description).join(', ')}`);
    }

    // Check for remaining steps
    const remainingSteps = this.currentTask.steps.filter(s => s.status === 'pending');
    if (remainingSteps.length > 0) {
      mustDocument.push(`Document ${remainingSteps.length} remaining steps`);
      warnings.push(`Task is ${this.currentTask.completionPercentage}% complete - ${remainingSteps.length} steps remaining`);
    }

    // Check for blocked steps
    const blockedSteps = this.currentTask.steps.filter(s => s.status === 'blocked');
    if (blockedSteps.length > 0) {
      mustDocument.push(`Document resolution needed for ${blockedSteps.length} blocked step(s)`);
    }

    // Check completion percentage
    if (this.currentTask.completionPercentage < 100) {
      warnings.push(`Task not complete (${this.currentTask.completionPercentage}%)`);
    }

    const canStop = mustDocument.length === 0 || this.currentTask.completionPercentage === 100;

    return {
      canStop,
      mustDocument,
      warnings,
      suggestedCheckpoint: {
        taskId: this.currentTask.id,
        currentStep: this.currentTask.steps.findIndex(s => s.status === 'in_progress') + 1,
        totalSteps: this.currentTask.steps.length,
        remainingSteps: remainingSteps,
        canResume: true,
      },
    };
  }

  /**
   * Create a checkpoint before stopping
   * REQUIRED if task is incomplete
   */
  createCheckpoint(
    resumptionNotes: string,
    contextSnapshot?: Partial<ContextSnapshot>
  ): TaskCheckpoint {
    if (!this.currentTask) {
      throw new Error('No active task to checkpoint');
    }

    const completedSteps = this.currentTask.steps
      .filter(s => s.status === 'completed')
      .map(s => s.id);

    const remainingSteps = this.currentTask.steps
      .filter(s => s.status === 'pending' || s.status === 'in_progress' || s.status === 'blocked');

    const checkpoint: TaskCheckpoint = {
      id: `checkpoint-${Date.now()}`,
      taskId: this.currentTask.id,
      timestamp: new Date().toISOString(),
      currentStep: completedSteps.length + 1,
      totalSteps: this.currentTask.steps.length,
      completedSteps,
      remainingSteps,
      contextSnapshot: {
        openFiles: contextSnapshot?.openFiles || [],
        lastModifiedFile: contextSnapshot?.lastModifiedFile,
        cursorPosition: contextSnapshot?.cursorPosition,
        relevantVariables: contextSnapshot?.relevantVariables,
        environmentState: contextSnapshot?.environmentState,
      },
      resumptionNotes,
      canResume: true,
    };

    this.currentTask.checkpoints.push(checkpoint);
    this.currentTask.status = 'paused';
    this.currentTask.updatedAt = new Date().toISOString();
    this.saveTasks();

    return checkpoint;
  }

  /**
   * Generate a task summary for handoff or resumption
   */
  generateSummary(): TaskSummary {
    if (!this.currentTask) {
      return {
        completed: [],
        inProgress: [],
        remaining: [],
        blocked: [],
        estimatedRemainingWork: 'No active task',
        nextSteps: [],
        warnings: ['No active task'],
      };
    }

    const completed = this.currentTask.steps
      .filter(s => s.status === 'completed')
      .map(s => `✓ ${s.description}${s.notes ? ` (${s.notes})` : ''}`);

    const inProgress = this.currentTask.steps
      .filter(s => s.status === 'in_progress')
      .map(s => `⏳ ${s.description}${s.notes ? ` (${s.notes})` : ''}`);

    const remaining = this.currentTask.steps
      .filter(s => s.status === 'pending')
      .map(s => `○ ${s.description}`);

    const blocked = this.currentTask.steps
      .filter(s => s.status === 'blocked')
      .map(s => `🚫 ${s.description}: ${s.blockedReason || 'Unknown reason'}`);

    const warnings: string[] = [];
    if (inProgress.length > 0) {
      warnings.push('Has in-progress work that needs attention');
    }
    if (blocked.length > 0) {
      warnings.push(`${blocked.length} step(s) are blocked`);
    }

    // Estimate remaining work
    const remainingCount = remaining.length + inProgress.length;
    const avgTimePerStep = 5; // minutes estimate
    const estimatedRemainingWork = remainingCount > 0
      ? `~${remainingCount * avgTimePerStep} minutes (${remainingCount} steps)`
      : 'Complete';

    // Determine next steps
    const nextSteps: string[] = [];
    if (inProgress.length > 0) {
      nextSteps.push(`Continue: ${this.currentTask.steps.find(s => s.status === 'in_progress')?.description}`);
    } else if (remaining.length > 0) {
      const nextPending = this.currentTask.steps.find(s => s.status === 'pending');
      if (nextPending) {
        nextSteps.push(`Start: ${nextPending.description}`);
      }
    }
    if (blocked.length > 0) {
      nextSteps.push(`Resolve blocked: ${this.currentTask.steps.find(s => s.status === 'blocked')?.description}`);
    }

    return {
      completed,
      inProgress,
      remaining,
      blocked,
      estimatedRemainingWork,
      nextSteps,
      warnings,
    };
  }

  /**
   * Generate markdown summary for documentation
   */
  generateMarkdownSummary(): string {
    const summary = this.generateSummary();
    const task = this.currentTask;

    if (!task) {
      return '## No Active Task\n\nNo task is currently being tracked.';
    }

    let md = `## Task Progress: ${task.title}\n\n`;
    md += `**Status:** ${task.status} | **Progress:** ${task.completionPercentage}%\n\n`;

    if (summary.completed.length > 0) {
      md += `### ✅ Completed (${summary.completed.length})\n`;
      summary.completed.forEach(s => md += `- ${s}\n`);
      md += '\n';
    }

    if (summary.inProgress.length > 0) {
      md += `### ⏳ In Progress (${summary.inProgress.length})\n`;
      summary.inProgress.forEach(s => md += `- ${s}\n`);
      md += '\n';
    }

    if (summary.remaining.length > 0) {
      md += `### 📋 Remaining (${summary.remaining.length})\n`;
      summary.remaining.forEach(s => md += `- ${s}\n`);
      md += '\n';
    }

    if (summary.blocked.length > 0) {
      md += `### 🚫 Blocked (${summary.blocked.length})\n`;
      summary.blocked.forEach(s => md += `- ${s}\n`);
      md += '\n';
    }

    if (summary.warnings.length > 0) {
      md += `### ⚠️ Warnings\n`;
      summary.warnings.forEach(w => md += `- ${w}\n`);
      md += '\n';
    }

    md += `### 📊 Metrics\n`;
    md += `- **Estimated Remaining:** ${summary.estimatedRemainingWork}\n`;
    md += `- **Last Updated:** ${task.updatedAt}\n`;

    if (summary.nextSteps.length > 0) {
      md += `\n### 👉 Next Steps\n`;
      summary.nextSteps.forEach((s, i) => md += `${i + 1}. ${s}\n`);
    }

    return md;
  }

  /**
   * Resume from a checkpoint
   */
  resumeFromCheckpoint(checkpointId: string): Task | null {
    for (const task of this.tasks.values()) {
      const checkpoint = task.checkpoints.find(c => c.id === checkpointId);
      if (checkpoint) {
        task.status = 'active';
        task.updatedAt = new Date().toISOString();
        this.currentTask = task;
        this.saveTasks();
        return task;
      }
    }
    return null;
  }

  /**
   * Resume the most recent paused task
   */
  resumeLatest(): Task | null {
    let latestTask: Task | null = null;
    let latestTime = 0;

    for (const task of this.tasks.values()) {
      if (task.status === 'paused') {
        const time = new Date(task.updatedAt).getTime();
        if (time > latestTime) {
          latestTime = time;
          latestTask = task;
        }
      }
    }

    if (latestTask) {
      latestTask.status = 'active';
      latestTask.updatedAt = new Date().toISOString();
      this.currentTask = latestTask;
      this.saveTasks();
    }

    return latestTask;
  }

  /**
   * Complete the current task
   */
  completeTask(): void {
    if (!this.currentTask) return;

    this.currentTask.status = 'completed';
    this.currentTask.completionPercentage = 100;
    this.currentTask.updatedAt = new Date().toISOString();

    // Mark all pending steps as completed
    this.currentTask.steps.forEach(s => {
      if (s.status === 'pending' || s.status === 'in_progress') {
        s.status = 'completed';
        s.completedAt = new Date().toISOString();
      }
    });

    this.saveTasks();
    this.currentTask = null;
  }

  /**
   * Abandon task (with documentation)
   */
  abandonTask(reason: string): TaskCheckpoint {
    if (!this.currentTask) {
      throw new Error('No active task to abandon');
    }

    const checkpoint = this.createCheckpoint(`ABANDONED: ${reason}`);
    this.currentTask.status = 'abandoned';
    this.saveTasks();
    this.currentTask = null;

    return checkpoint;
  }

  /**
   * Get current task
   */
  getCurrentTask(): Task | null {
    return this.currentTask;
  }

  /**
   * Get all tasks
   */
  getAllTasks(): Task[] {
    return Array.from(this.tasks.values());
  }

  /**
   * Get paused tasks
   */
  getPausedTasks(): Task[] {
    return Array.from(this.tasks.values()).filter(t => t.status === 'paused');
  }

  /**
   * Load task by ID
   */
  loadTask(taskId: string): Task | null {
    const task = this.tasks.get(taskId);
    if (task) {
      this.currentTask = task;
      return task;
    }
    return null;
  }

  private updateCompletionPercentage(): void {
    if (!this.currentTask || this.currentTask.steps.length === 0) return;

    const completed = this.currentTask.steps.filter(s => s.status === 'completed').length;
    this.currentTask.completionPercentage = Math.round((completed / this.currentTask.steps.length) * 100);
  }

  private loadTasks(): void {
    try {
      if (fs.existsSync(this.tasksPath)) {
        const data = JSON.parse(fs.readFileSync(this.tasksPath, 'utf-8'));
        this.tasks = new Map(Object.entries(data.tasks || {}));
        
        // Restore current task if it was active
        if (data.currentTaskId) {
          this.currentTask = this.tasks.get(data.currentTaskId) || null;
        }
      }
    } catch (e) {
      console.error('Failed to load tasks:', e);
    }
  }

  private saveTasks(): void {
    try {
      const dir = path.dirname(this.tasksPath);
      if (!fs.existsSync(dir)) {
        fs.mkdirSync(dir, { recursive: true });
      }

      const data = {
        tasks: Object.fromEntries(this.tasks),
        currentTaskId: this.currentTask?.id,
        lastUpdated: new Date().toISOString(),
      };

      fs.writeFileSync(this.tasksPath, JSON.stringify(data, null, 2));
    } catch (e) {
      console.error('Failed to save tasks:', e);
    }
  }
}

/**
 * Create task tracker instance
 */
export function createTaskTracker(workspaceRoot: string): TaskTracker {
  return new TaskTracker(workspaceRoot);
}
